import { call, put, select, take, takeEvery } from 'typed-redux-saga';

import { sendCloudApiCommand } from '../../../../shared/features/moderation/sagas';
import {
    hideNotification,
    NOTIFICATIONS_MAP,
    notify,
} from '../../../../shared/features/notifications/ps-notifications';
import { isMobileUa } from '../../../../shared/features/uatraits';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { CloudAPI } from '../../../../shared/helpers/transport/cloud-api/types';
import { CloudRecordingStatus } from '../../../../types';
import { NotificationId } from '../../types';
import {
    closeCloudRecordingStopModalAction,
    setCloudRecordingStatus,
    setIsCloudRecordingStopModalOpened,
    startCloudRecordingAction,
    stopCloudRecordingAction,
} from './slice';

const logger = createLogger('cloud-recording');

type CloudRecordingCommand = Extract<CloudAPI.SendCommand.ApiCommand, 'start-cloud-recording' | 'stop-cloud-recording'>;

const errorNotificationsGettersMap = {
    'start-cloud-recording': NOTIFICATIONS_MAP[NotificationId.CLOUD_RECORDING_START_FAILED],
    'stop-cloud-recording': NOTIFICATIONS_MAP[NotificationId.CLOUD_RECORDING_STOP_FAILED],
};

const notificationsGettersMap = {
    RUNNING: NOTIFICATIONS_MAP[NotificationId.CLOUD_RECORDING_START],
    TURNED_OFF: NOTIFICATIONS_MAP[NotificationId.CLOUD_RECORDING_STOP],
};

function* getErrorNotification(command: CloudRecordingCommand) {
    const isMobile = yield* select(isMobileUa);

    return errorNotificationsGettersMap[command](isMobile);
}

function* countCloudRecordingMetrica(command: string) {
    yield* call(countMetrika, 'meeting_screen', 'cloud-recording', command);
}

function* sendCloudRecordingCommandSaga(command: CloudRecordingCommand) {
    try {
        yield* call(sendCloudApiCommand, command, {});
        yield* call(countCloudRecordingMetrica, command);
    } catch (error) {
        logger.error(error, { method: 'sendCloudRecordingCommandSaga', additional: { command } });

        const errorNotification = yield* getErrorNotification(command);

        yield* put(notify(errorNotification));
    }
}

function* startCloudRecordingSaga() {
    yield* call(sendCloudRecordingCommandSaga, 'start-cloud-recording');
}

function* showStopCloudRecordingModal() {
    yield* put(setIsCloudRecordingStopModalOpened(true));

    const { payload } = yield* take(closeCloudRecordingStopModalAction);

    yield* put(setIsCloudRecordingStopModalOpened(false));

    return payload;
}

function* stopCloudRecordingSaga() {
    const isConfirmed = yield* call(showStopCloudRecordingModal);

    if (isConfirmed) {
        yield* call(sendCloudRecordingCommandSaga, 'stop-cloud-recording');
    }
}

function* hideCloudRecordingNotifications() {
    yield* put(hideNotification(NotificationId.CLOUD_RECORDING_START));
    yield* put(hideNotification(NotificationId.CLOUD_RECORDING_START_FAILED));
    yield* put(hideNotification(NotificationId.CLOUD_RECORDING_STOP));
    yield* put(hideNotification(NotificationId.CLOUD_RECORDING_STOP_FAILED));
}

export function* updateCloudRecordingStatus(cloudRecordingStatus: CloudRecordingStatus) {
    yield* put(setCloudRecordingStatus(cloudRecordingStatus));

    yield* hideCloudRecordingNotifications();

    if (cloudRecordingStatus !== 'STARTING') {
        const notification = notificationsGettersMap[cloudRecordingStatus]();

        yield* put(notify(notification));
    }
}

export function* cloudRecordingSagas() {
    yield* takeEvery(startCloudRecordingAction, startCloudRecordingSaga);
    yield* takeEvery(stopCloudRecordingAction, stopCloudRecordingSaga);
}
